import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from './provider.js';

/**
 * Bloom: single source of truth for "who sent this message?" display.
 * Returns a normalized name, an avatar URL, and the underlying email so
 * callers don't have to chase fallback chains in three different files
 * (EmailMeta, EmailBubble, SenderHeader, TextBubble).
 *
 * Fallback order for name:
 *   1. sender.name
 *   2. content_attributes.email.from[0]
 *   3. i18n CONVERSATION.UNKNOWN_SENDER
 */
export function useMessageSender() {
  const { sender, contentAttributes } = useMessageContext();
  const { t } = useI18n();

  const fromEmail = computed(
    () => contentAttributes.value?.email?.from?.[0] ?? sender.value?.email ?? ''
  );

  const name = computed(
    () =>
      sender.value?.name || fromEmail.value || t('CONVERSATION.UNKNOWN_SENDER')
  );

  const avatarUrl = computed(() => sender.value?.thumbnail || '');

  // True when the "From:" address differs from the resolved sender's own
  // email — useful for the EmailMeta "Name <email>" line so we don't show
  // a redundant address when they match.
  const hasDistinctFromAddress = computed(() => {
    if (!fromEmail.value) return false;
    if (!sender.value?.name) return true;
    return sender.value.email !== fromEmail.value;
  });

  return { name, avatarUrl, fromEmail, hasDistinctFromAddress };
}
